import { useState } from 'react'
import { toast } from 'sonner'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceInfo,
  PreferenceInput,
  PreferenceScreen,
  PreferenceSubmit,
} from '../../components/Preference'
import { authClient, useMe } from '../../lib/auth-client'

export default function PasswordSettings() {
  const { data: me, mutate: mutateMe } = useMe()
  const hasPassword = me?.hasPassword ?? false
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [busy, setBusy] = useState(false)

  async function savePassword() {
    setBusy(true)
    try {
      if (newPassword.length < 8) {
        toast.error('Wachtwoord moet minstens 8 tekens zijn')
        return
      }
      if (newPassword !== confirmPassword) {
        toast.error('Wachtwoorden komen niet overeen')
        return
      }
      if (hasPassword) {
        const { error: err } = await authClient.changePassword({
          currentPassword,
          newPassword,
          revokeOtherSessions: true,
        })
        if (err) {
          toast.error(err.message || 'Wachtwoord wijzigen mislukt')
          return
        }
      } else {
        const res = await fetch('/api/set-password', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ newPassword }),
        })
        const data = await res.json()
        if (!res.ok) {
          toast.error(data.message || 'Wachtwoord instellen mislukt')
          return
        }
      }
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      toast.success('Wachtwoord bewaard')
      await mutateMe()
    } finally {
      setBusy(false)
    }
  }

  async function requestReset() {
    if (!me?.user?.email) return
    setBusy(true)
    try {
      const { error: err } = await authClient.requestPasswordReset({
        email: me.user.email,
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (err) {
        toast.error(err.message || 'Resetlink versturen mislukt')
        return
      }
      toast.success('Resetlink verstuurd', {
        description: 'Check je inbox voor een resetlink.',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <PreferenceScreen title="Wachtwoord" backHref="/settings/account">
      <PreferenceCategory filled>
        {hasPassword ? (
          <PreferenceInput
            title="Huidig wachtwoord"
            type="password"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            minLength={8}
            placeholder="••••••••"
          />
        ) : (
          <PreferenceInfo
            title="Nog geen wachtwoord"
            summary="Stel een wachtwoord in om ook met e-mail in te loggen."
          />
        )}
        <PreferenceInput
          title="Nieuw wachtwoord"
          type="password"
          value={newPassword}
          onChange={setNewPassword}
          autoComplete="new-password"
          minLength={8}
          placeholder="••••••••"
        />
        <PreferenceInput
          title="Bevestig wachtwoord"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          minLength={8}
          placeholder="••••••••"
        />
        <PreferenceSubmit
          title={hasPassword ? 'Wachtwoord wijzigen' : 'Wachtwoord instellen'}
          onPress={savePassword}
          disabled={
            busy ||
            !confirmPassword ||
            !newPassword ||
            (hasPassword && !currentPassword)
          }
        />
      </PreferenceCategory>

      {hasPassword ? (
        <PreferenceCategory filled>
          <PreferenceButton
            title="Wachtwoord vergeten?"
            summary="Stuur een resetlink naar je e-mail"
            onPress={requestReset}
            disabled={busy}
          />
        </PreferenceCategory>
      ) : null}
    </PreferenceScreen>
  )
}
